import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Group, Loader, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceDesktop, IconDownload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { ImageUploadInput } from '../../components/ImageUploadInput';

// Business branding the owner controls for their own tenant: display
// names + logo (shown in the backoffice topbar and on receipts). The
// custom-domain binding is a platform-admin action (needs DNS), shown
// here read-only when set.

interface BusinessProfile {
  businessNameAr: string;
  businessNameEn: string | null;
  logoUrl: string | null;
  currency: string;
  customDomain: string | null;
}

interface DesktopInstaller {
  available: boolean;
  version: string | null;
  downloadUrl: string | null;
  releaseDate: string | null;
}

export function SettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['business-profile'] as const,
    queryFn: async () => (await apiClient.get<BusinessProfile>('/api/me/business')).data,
  });

  const installer = useQuery({
    queryKey: ['desktop-installer'] as const,
    queryFn: async () => (await apiClient.get<DesktopInstaller>('/api/app/desktop-installer')).data,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data) {
      setNameAr(data.businessNameAr);
      setNameEn(data.businessNameEn ?? '');
      setLogoUrl(data.logoUrl ?? '');
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () =>
      apiClient.put('/api/me/business', {
        businessNameAr: nameAr.trim(),
        businessNameEn: nameEn.trim() || null,
        logoUrl: logoUrl.trim() || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['business-profile'] });
      void qc.invalidateQueries({ queryKey: ['business-branding'] });
      notifications.show({ color: 'green', title: t('settings.saved'), message: '' });
    },
    onError: () =>
      notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });

  // Logo auto-save: fires right after an upload (or remove) so no Save press
  // is needed. Persists ONLY the logo — names come from the stored profile so
  // unsaved name edits aren't committed as a side effect.
  const saveLogo = useMutation({
    mutationFn: async (url: string | null) =>
      apiClient.put('/api/me/business', {
        businessNameAr: data?.businessNameAr ?? nameAr.trim(),
        businessNameEn: data?.businessNameEn ?? null,
        logoUrl: url,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['business-profile'] });
      void qc.invalidateQueries({ queryKey: ['business-branding'] });
      notifications.show({ color: 'green', title: t('settings.saved'), message: '' });
    },
    onError: () =>
      notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });

  return (
    <Stack gap="lg">
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      {isLoading ? (
        <Group justify="center" py="xl"><Loader color="wheelsRed" /></Group>
      ) : (
        <Paper p="lg" maw={640}>
          <Stack gap="md">
            <TextInput
              label={t('settings.nameArLabel')}
              value={nameAr}
              onChange={(e) => setNameAr(e.currentTarget.value)}
              withAsterisk
            />
            <TextInput
              label={t('settings.nameEnLabel')}
              value={nameEn}
              onChange={(e) => setNameEn(e.currentTarget.value)}
              dir="ltr"
            />
            <ImageUploadInput
              label={t('settings.logoUrlLabel')}
              kind="logo"
              value={logoUrl.trim() || null}
              onChange={(url) => {
                setLogoUrl(url ?? '');
                saveLogo.mutate(url);
              }}
            />
            {data?.customDomain && (
              <Text size="sm" c="dimmed">
                {t('settings.customDomain')}: <span dir="ltr">{data.customDomain}</span>
              </Text>
            )}
            <Group justify="flex-end">
              <Button
                color="wheels"
                loading={save.isPending}
                disabled={!nameAr.trim()}
                onClick={() => save.mutate()}
              >
                {t('common.save')}
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      {/* Desktop Cashier (Windows) installer — always links to the latest build. */}
      <Paper p="lg" maw={640}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" wrap="nowrap">
            <IconDeviceDesktop size={28} stroke={1.5} color="var(--wheels-accent, #B5191D)" />
            <div>
              <Title order={5} c="var(--wheels-ink-strong)">{t('settings.desktop.title')}</Title>
              <Text size="sm" c="dimmed">{t('settings.desktop.subtitle')}</Text>
              {installer.data?.available && installer.data.version && (
                <Group gap={6} mt={6}>
                  <Badge variant="light" color="gray" radius="sm" className="font-numeric">v{installer.data.version}</Badge>
                  {installer.data.releaseDate && (
                    <Text size="xs" c="dimmed" className="font-numeric">
                      {new Intl.DateTimeFormat('ar-PS', { dateStyle: 'medium', numberingSystem: 'latn' }).format(new Date(installer.data.releaseDate))}
                    </Text>
                  )}
                </Group>
              )}
            </div>
          </Group>
          {installer.isLoading ? (
            <Loader size="sm" color="wheels" />
          ) : installer.data?.available && installer.data.downloadUrl ? (
            <Button
              color="wheels"
              component="a"
              href={installer.data.downloadUrl}
              leftSection={<IconDownload size={16} stroke={1.8} />}
            >
              {t('settings.desktop.download')}
            </Button>
          ) : (
            <Badge variant="light" color="yellow" radius="sm">{t('settings.desktop.unavailable')}</Badge>
          )}
        </Group>
      </Paper>
    </Stack>
  );
}
